import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Button, SelectOption } from '@factorialco/deprecated-design-system'
import { A3innuvaSyncCompanyLegalEntitiesByPolicyIdDocument } from 'generated/resources/graphql'
import param from 'jquery-param'
import moment from 'moment'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import SelectOrCreate from 'modules/core/components/SelectOrCreate'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FACTORIAL_APP, PROFESSIONALS_APP } from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'
import { downloadFile } from 'modules/payroll_exporter/lib/downloadFile'
import Relink from 'modules/payroll_integrations_a3innuva/components/Relink'
import authService, {
  Status as AuthStatus,
  LOGIN_PATH,
} from 'modules/payroll_integrations_a3innuva/lib/OauthService'
import { ExportFormat } from 'modules/payroll_integrations_a3innuva/lib/types'

interface ExportParams {
  type: string
  from: string
  to: string
  legal_entity: string | null
  update_export_period_type: string
  payroll_policy_period_id: string | null
  company_id: string | null
  bookkeeper_view: boolean
  update_from?: string
  update_to?: string
  employee_ids?: string[]
  timestamp: number
}

const PERIOD_ALL = 'all'
const DEFAULT_TYPE = ExportFormat.A3INNUVA_SUPPLEMENTS

interface Props {
  onClose: () => void
  selectedCompany?: string | null
  isProfessionals?: boolean
}
const SyncModal = ({ selectedCompany = null, isProfessionals = false, onClose }: Props) => {
  const { currentPolicyPeriods, periodIndex, policyId: contextPolicyId } = usePolicyPeriods()
  const session = useCurrentSession()
  const [disabled, setDisabled] = useState<boolean>(true)
  const [legalEntity, setLegalEntity] = useState<string | null>(null)
  const { policyId: paramsPolicyId } = useParams()

  const policyId: string = paramsPolicyId ? String(paramsPolicyId) : contextPolicyId ?? '0'
  let variables = {}
  if (selectedCompany) {
    variables = { ...variables, companiesIds: [selectedCompany] }
  }
  if (policyId != '0') {
    variables = { payrollPolicyIds: [policyId] }
  }
  const { data: legalEntitiesData } = useQuery(A3innuvaSyncCompanyLegalEntitiesByPolicyIdDocument, {
    variables: variables,
    skip: policyId == '0' && !selectedCompany,
  })
  let companyId: string | null | undefined = null
  let legalEntityList: SelectOption[] = []
  let selectedCompanyName = ''
  if (legalEntitiesData) {
    const payrollPolicyCompanyId: string | null | undefined =
      legalEntitiesData.contracts.payrollPoliciesConnection.nodes[0]?.policy?.company.id
    companyId = selectedCompany || payrollPolicyCompanyId
    legalEntityList =
      legalEntitiesData.contracts.payrollPoliciesConnection.nodes[0]?.policy?.company.legalentitiesConnection?.nodes?.map(
        (element) => {
          return {
            label: element.legalName,
            value: element.id,
          }
        }
      ) ?? []
    selectedCompanyName =
      legalEntitiesData.contracts.payrollPoliciesConnection.nodes[0]?.policy?.company
        .legalentitiesConnection?.nodes?.[0]?.company.name ?? ''
  } else {
    companyId = session.company.id
    selectedCompanyName = session.company.name
    legalEntityList = session.company.legalEntities.map((element) => {
      return {
        label: element.legalName,
        value: element.id,
      }
    })
  }
  const le_count: number = legalEntityList.length
  const first_le = legalEntityList[0]?.value
  useEffect(() => {
    if (le_count === 1) {
      setLegalEntity(first_le)
    }
  }, [le_count, first_le])
  const currentPolicyPeriod: PolicyPeriod | undefined = currentPolicyPeriods.at(periodIndex)
  const [fromDate, setFromDate] = useState<moment.Moment>(
    currentPolicyPeriod ? currentPolicyPeriod.attributes.starts_on : moment().startOf('month')
  )
  const [toDate, setToDate] = useState<moment.Moment>(
    currentPolicyPeriod ? currentPolicyPeriod.attributes.ends_on : moment().endOf('month')
  )
  const [updateFromDate, setUpdateFromDate] = useState<moment.Moment>(moment().startOf('month'))
  const [updateToDate, setUpdateToDate] = useState<moment.Moment>(moment().endOf('month'))
  useEffect(() => {
    if (currentPolicyPeriod) {
      const startsOn = stringToDate(currentPolicyPeriod.attributes.starts_on, 'backend')
      const endsOn = stringToDate(currentPolicyPeriod.attributes.ends_on, 'backend')
      setToDate(endsOn)
      setFromDate(startsOn)
      setUpdateFromDate(startsOn)
      setUpdateToDate(endsOn)
      fetchAuthStatus()
    }
  }, [periodIndex, legalEntity]) // eslint-disable-line react-hooks/exhaustive-deps
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.NotAvailable)
  const isBookkeeperView = useBookkeeperView()
  const fetchAuthStatus = async () => {
    if (companyId) {
      const currentAuthStatus = await authService.getStatus(
        {
          companyId: companyId,
        },
        isBookkeeperView
      )
      if (currentAuthStatus != AuthStatus.Authenticated || !legalEntity) {
        setDisabled(true)
      } else {
        setDisabled(false)
      }
      setAuthStatus(currentAuthStatus)
    }
  }
  if (!companyId) {
    app.setFlash({ type: 'wrong', message: i18n.t('payroll_bundle.export.error') })
    onClose()
    return null
  }
  const useConnect = () => {
    const tryWithIframe = () => {
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none' // Hide iframe
      iframe.src = buildUrl(LOGIN_PATH) // Login URL
      document.body.appendChild(iframe)
      const handleIframeError = () => {
        // Clean iframe if an error
        document.body.removeChild(iframe)
        console.error('Iframe login failed due to X-Frame-Options. Falling back to popup.') // eslint-disable-line no-console
        tryWithPopup() // Try with popup
      }
      iframe.onload = () => {
        try {
          // Check if login in iframe was successfull
          document.body.removeChild(iframe) // Remove iframe
          track(trackingEvents.payrollIntegrationsA3Innuva.a3innuvaSettingsLogin, {
            company_id: companyId,
            access_id: session.access.userId,
          })
          fetchAuthStatus() // Verify authentication status
        } catch (e) {
          handleIframeError()
        }
      }
    }
    const tryWithPopup = () => {
      const popup = window.open(buildUrl(LOGIN_PATH), '_blank', 'width=500,height=600')
      if (!popup) {
        console.error('Popup could not be opened. Please allow popups in your browser.') // eslint-disable-line no-console
        return
      }
      const interval = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(interval)
            // Verify authentication status
            fetchAuthStatus()
            track(trackingEvents.payrollIntegrationsA3Innuva.a3innuvaSettingsLogin, {
              company_id: companyId,
              access_id: session.access.userId,
            })
          }
        } catch (e) {
          console.error('Error checking popup status:', e) // eslint-disable-line no-console
        }
      }, 500)
    }
    // Try first with iframe
    tryWithIframe()
  }
  const onDownload = async () => {
    setDisabled(true)

    let data: ExportParams = {
      type: DEFAULT_TYPE,
      from: dateToString(fromDate, 'backend'),
      to: dateToString(toDate, 'backend'),
      legal_entity: legalEntity,
      update_export_period_type: PERIOD_ALL,
      payroll_policy_period_id: null,
      company_id: companyId,
      bookkeeper_view: isProfessionals,
      /* Used to identify the error export with its export
      for different requests with the same params */
      timestamp: Date.now(),
    }
    if (currentPolicyPeriod) {
      data.payroll_policy_period_id = currentPolicyPeriod.attributes.id
    }
    if (updateFromDate && updateToDate) {
      data = {
        ...data,
        update_from: dateToString(updateFromDate, 'backend'),
        update_to: dateToString(updateToDate, 'backend'),
      }
    }
    await downloadFile(param({ ...data, v: 2 }), data)

    track(trackingEvents.payroll.syncData, {
      sync_format: DEFAULT_TYPE,
      updates: PERIOD_ALL,
      company_id: companyId,
      company_name: selectedCompanyName,
      period: currentPolicyPeriod?.attributes.name,
      period_id: currentPolicyPeriod?.attributes.id,
      application: isProfessionals ? PROFESSIONALS_APP : FACTORIAL_APP,
    })
    setDisabled(false)
    onClose()
  }
  const isDisabled = () => {
    return disabled
  }

  function modal() {
    return (
      <Modal
        title={i18n.t('payroll_integrations.sync.sync_heading', {
          integrationName: 'a3innuva',
        })}
        illustration="URL"
        onClose={onClose}
        footer={
          <>
            <Button onClick={onClose} type="secondary">
              {i18n.t('common.cancel')}
            </Button>
            <Button onClick={onDownload} type="primary" disabled={isDisabled()}>
              {i18n.t('payroll_integrations.sync.sync_confirm')}
            </Button>
          </>
        }
      >
        <Box gap="s16">
          {legalEntityList.length && (
            <SelectOrCreate.Popover
              label={i18n.t('payroll_integrations.sync.legal_entity')}
              placeholder={i18n.t('payroll_integrations.sync.sync_legal_entity_placeholder')}
              values={legalEntity ? [legalEntity] : []}
              onValuesChange={(newValues: string[]) => setLegalEntity(newValues[0] || null)}
              onChange={() => {}}
              isMultiSelect={false}
            >
              {legalEntityList.map((option) => (
                <SelectOrCreate.Option
                  key={option.value}
                  label={option.label}
                  searchValue={option.label}
                  value={option.value}
                >
                  {option.label}
                </SelectOrCreate.Option>
              ))}
            </SelectOrCreate.Popover>
          )}
          {!(authStatus == AuthStatus.NotAvailable || authStatus == AuthStatus.Authenticated) && (
            <Relink onClick={useConnect} blockedSubmit={false} integrationLabel={'A3innuva'} />
          )}
          {
            <Callout
              type="warning"
              description={i18n.t('payroll_integrations.sync.sync_modal_warning')}
            />
          }
        </Box>
      </Modal>
    )
  }
  const modalCanBeShown = periodIndex != null
  return <>{modalCanBeShown && modal()}</>
}
export default SyncModal
